/* eslint-disable react/no-unstable-nested-components */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  SimpleGrid,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Tooltip,
  Icon,
} from '@chakra-ui/react';
import { Info } from 'phosphor-react';
import { DataGrid } from '../../components/datagrid';
import { SearchFilter } from '../../components/inputs/filters/search';
import { PageTitle } from '../../components/PageTitle';
import { useFetch } from '../../hooks/useFetch';
import { getCellStatus } from '../../utils/FormatFunctions';
import { useAuth } from '../../providers/auth';
import { api } from '../../services/api';

interface TemplatesSummary {
  institutionsTotal: number;
  institutionsManual: number;
  institutionsDefault: number;
  institutionsWithoutTemplates: number;
  extraMaterialsTotal: number;
  topicsTotal: number;
  modulesTotal: number;
  categoriesTotal: number;
}

export function TemplatesPage() {
  const baseApiUrl = '/v1/admin';
  const baseUrl = `${baseApiUrl}/extra-material-templates`;
  const { requestApi, data, loading, error } = useFetch(baseUrl);
  const { user } = useAuth();
  const [summary, setSummary] = useState<TemplatesSummary>({
    institutionsTotal: 0,
    institutionsManual: 0,
    institutionsDefault: 0,
    institutionsWithoutTemplates: 0,
    extraMaterialsTotal: 0,
    topicsTotal: 0,
    modulesTotal: 0,
    categoriesTotal: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const columns = [
    { name: 'id', label: 'Id' },
    { name: 'name', label: 'Nome' },
    { name: 'type', label: 'Tipo' },
    { name: 'user_type', label: 'Material para' },
    { name: 'school_class.name', label: 'Turma' },
    { name: 'category.name', label: 'Categoria' },
    { name: 'is_official', label: 'Oficial' },
    { name: 'version', label: 'Versão' },
    { name: 'status', label: 'Status' },
  ];

  const groupIds = user?.group?.id;

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const response = await api.get('/v1/admin/templates/summary');
      const payload = response.data?.data ?? {};
      setSummary({
        institutionsTotal: payload.institutions_total ?? 0,
        institutionsManual: payload.institutions_manual ?? 0,
        institutionsDefault: payload.institutions_default ?? 0,
        institutionsWithoutTemplates: payload.institutions_without_templates ?? 0,
        extraMaterialsTotal: payload.extra_materials_total ?? 0,
        topicsTotal: payload.topics_total ?? 0,
        modulesTotal: payload.modules_total ?? 0,
        categoriesTotal: payload.categories_total ?? 0,
      });
    } catch (err: any) {
      setSummaryError(
        err?.response?.data?.msg ?? 'Não foi possível carregar o resumo dos templates.'
      );
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const summaryCards = useMemo(
    () => [
      {
        key: 'institutionsTotal',
        label: 'Instituições cadastradas',
        value: summary.institutionsTotal,
        tooltip: 'Total de instituições de ensino cadastradas no sistema',
      },
      {
        key: 'institutionsDefault',
        label: 'Instituições com template padrão',
        value: summary.institutionsDefault,
        tooltip:
          'Instituições que utilizam o template padrão do sistema, com todos os materiais oficiais pré-configurados',
      },
      {
        key: 'institutionsManual',
        label: 'Instituições com template personalizado',
        value: summary.institutionsManual,
        tooltip:
          'Instituições que possuem templates customizados, permitindo escolher quais materiais e tópicos disponibilizar',
      },
      {
        key: 'institutionsWithoutTemplates',
        label: 'Instituições sem templates vinculados',
        value: summary.institutionsWithoutTemplates,
        tooltip:
          'Instituições que ainda não possuem nenhum template vinculado e precisam de configuração',
      },
      {
        key: 'extraMaterials',
        label: 'Materiais extras',
        value: summary.extraMaterialsTotal,
        tooltip: 'Conteúdos complementares (PDFs, vídeos, links) disponíveis nos templates',
      },
      {
        key: 'topics',
        label: 'Tópicos',
        value: summary.topicsTotal,
        tooltip: 'Assuntos pedagógicos específicos que organizam os materiais extras',
      },
      {
        key: 'modules',
        label: 'Módulos',
        value: summary.modulesTotal,
        tooltip:
          'Agrupamentos pedagógicos que contêm conjuntos de materiais relacionados (ex: Fundamental I)',
      },
      {
        key: 'categories',
        label: 'Categorias',
        value: summary.categoriesTotal,
        tooltip: 'Classificações que organizam os materiais por tipo ou finalidade',
      },
    ],
    [summary]
  );

  return (
    <Box>
      <PageTitle>Gestão de Templates</PageTitle>

      <Box mb={6}>
        {summaryError && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            {summaryError}
          </Alert>
        )}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          {summaryCards.map((card) => (
            <Box key={card.key} borderRadius="lg" borderWidth="1px" p={4} bg="white" boxShadow="sm">
              {summaryLoading ? (
                <Skeleton height="48px" />
              ) : (
                <Stat>
                  <StatLabel display="flex" alignItems="center" gap={2}>
                    {card.label}
                    <Tooltip label={card.tooltip} fontSize="sm" hasArrow placement="top">
                      <Box display="inline-flex" cursor="help">
                        <Icon as={Info} boxSize={4} color="gray.400" />
                      </Box>
                    </Tooltip>
                  </StatLabel>
                  <StatNumber>{card.value.toLocaleString('pt-BR')}</StatNumber>
                </Stat>
              )}
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      <DataGrid
        columns={columns}
        rows={data?.data}
        requestApi={requestApi}
        error={error}
        baseUrl={baseUrl}
        data={data}
        hasCreate={groupIds === 1} // Only Sistema group can create templates
        loading={loading}
        rowActions={{
          show: false,
          edit: groupIds === 1, // Only Sistema group can edit
          delete: groupIds === 1, // Only Sistema group can delete
        }}
        formatColum={[
          {
            field: 'status',
            formatField: getCellStatus,
          },
          {
            field: 'is_official',
            formatField: (value: boolean) => (
              <Badge colorScheme={value ? 'green' : 'gray'}>{value ? 'Sim' : 'Não'}</Badge>
            ),
          },
          {
            field: 'user_type',
            formatField: (value: string) => {
              const userTypes: Record<string, string> = {
                TEACHER: 'Professor',
                STUDENT: 'Aluno',
                TODOS: 'Todos',
              };
              return userTypes[value] || value;
            },
          },
        ]}
        headerFilters={[
          {
            name: 'search',
            filterComponent: SearchFilter,
          },
        ]}
      />
    </Box>
  );
}
